import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";

const COLORS = ["#b40023", "#7c3aed", "#0ea5e9", "#f59e0b", "#10b981", "#ec4899", "#f97316", "#14b8a6"];

// Generic server-result wheel: segments = [{id,label}], targetId = pre-determined winner.
// The animation only visualises the already-known result.
export const SpinWheel = ({ segments, targetId, size = 520, duration = 4.5, onDone, title, testId = "spin-wheel" }) => {
  const [rotation, setRotation] = useState(0);
  const [done, setDone] = useState(false);
  const doneRef = useRef(false);
  const n = Math.max(segments.length, 1);
  const seg = 360 / n;
  const targetIndex = Math.max(0, segments.findIndex(s => s.id === targetId));

  const gradient = useMemo(() => {
    const parts = segments.map((s, i) => `${COLORS[i % COLORS.length]} ${i * seg}deg ${(i + 1) * seg}deg`);
    return `conic-gradient(${parts.join(", ")})`;
  }, [segments, seg]);

  useEffect(() => {
    doneRef.current = false;
    setDone(false);
    // pointer is at top (0deg). Segment i center is at i*seg + seg/2. Rotate so target center lands at top.
    const target = 360 * 6 - (targetIndex * seg + seg / 2) + (Math.random() * seg * 0.6 - seg * 0.3);
    const t = setTimeout(() => setRotation(target), 50);
    const d = setTimeout(() => { if (!doneRef.current) { doneRef.current = true; setDone(true); onDone && onDone(segments[targetIndex]); } }, duration * 1000 + 300);
    return () => { clearTimeout(t); clearTimeout(d); };
  }, [targetId, segments.length]);

  return (
    <div className="flex flex-col items-center gap-6" data-testid={testId}>
      {title && <p className="text-sm uppercase tracking-[0.4em] text-white/50">{title}</p>}
      <div className="relative" style={{ width: size, height: size }}>
        <div className="absolute left-1/2 -translate-x-1/2 -top-3 z-20 w-0 h-0 border-l-[18px] border-r-[18px] border-t-[34px] border-l-transparent border-r-transparent border-t-white drop-shadow-lg" />
        <motion.div
          className="rounded-full w-full h-full shadow-[0_0_80px_rgba(0,0,0,0.6)] border-8 border-white/20"
          style={{ background: gradient }}
          animate={{ rotate: rotation }}
          transition={{ duration, ease: [0.12, 0.8, 0.2, 1] }}
        >
          {segments.map((s, i) => (
            <div
              key={s.id}
              className="absolute left-1/2 top-1/2 origin-left text-white font-bold drop-shadow"
              style={{
                transform: `rotate(${i * seg + seg / 2 - 90}deg) translateX(${size * 0.16}px)`,
                width: size * 0.3,
                fontSize: n > 12 ? 13 : n > 8 ? 16 : 20,
              }}
            >
              <span className="block truncate">{s.label}</span>
            </div>
          ))}
        </motion.div>
        <div className="absolute inset-0 m-auto w-16 h-16 rounded-full bg-black border-4 border-white/40 z-10" />
      </div>
      {done && (
        <motion.p initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-5xl font-black text-amber-300" data-testid={`${testId}-result`}>
          {segments[targetIndex]?.label}
        </motion.p>
      )}
    </div>
  );
};
